import { Actions, createEffect, ofType } from "@ngrx/effects";
import { InstallationActions } from "./installation-state.actions";
import { inject, Injectable } from "@angular/core";
import { catchError, EMPTY, exhaustMap, filter, map, of, switchMap, tap, withLatestFrom } from "rxjs";
import { InstallationsApiService } from "../../services/api-services/installations/installations.api-service";
import { InstallationTreeApiService } from "../../services/api-services/installation-tree/installation-tree.api-service";
import { InstallationSelectors } from "./installation-state.selectors";
import { routerSelectors } from "../routeState/router.selector";
import { Store } from "@ngrx/store";


@Injectable()
export class InstallationStateEffects {
    store: Store = inject(Store);
    actions$: Actions = inject(Actions);
    installationService: InstallationsApiService = inject(InstallationsApiService);
    private treeApi: InstallationTreeApiService = inject(InstallationTreeApiService);

    /** Tree edits (details/rename/move) refresh the selected-installation card in the sidebar -
     *  its store copy is loaded once per context and would otherwise go stale. Fires AFTER the
     *  api service's cache clear (that subscription registered first), so the fetch is fresh. */
    refreshActiveInstallationOnTreeMutation$ = createEffect(() =>
        this.treeApi.mutated$.pipe(
            withLatestFrom(this.store.select(routerSelectors.selectInstallationRefCode)),
            map(([, refCode]) => refCode),
            filter((refCode): refCode is string => !!refCode),
            switchMap((refCode) => this.installationService.getInstallationDetails(refCode).pipe(
                map((installation) => InstallationActions.refreshActiveInstallationDetailsSuccess({ installation })),
                catchError(() => EMPTY)
            ))
        )
    );

    getLatestInstallations$ = createEffect(() =>
        this.actions$.pipe(ofType(InstallationActions.getLatestInstallations),
            withLatestFrom(
                this.store.select(InstallationSelectors.getLatestInstallations).pipe(
                    map(latest => latest ?? []) // Always return a list
                )
            ),
            exhaustMap(([_, latest]) => {
                // If the latest has already been loaded, we don't want to load it again. 
                // After first load, it will be updated on Add
                if (latest && latest.length > 0) {
                    return EMPTY;
                }

                return this.installationService.getLatestInstallationsNew()
                    .pipe(
                        map((installations) =>
                            InstallationActions.getLatestInstallationsSuccess({ installations: installations })
                        ),
                        catchError((error) =>
                            of(InstallationActions.getLatestInstallationsFailed({ error: error }))
                        )
                    )
            })
        )
    );

    addLatestInstallation$ = createEffect(() =>
        this.actions$.pipe(ofType(InstallationActions.addLatestInstallation),
            switchMap((action) => {
                return this.installationService.addLatestInstallations(action.refCode).pipe(
                    map((installations) => InstallationActions.addLatestInstallationSuccess({ installations: installations }))
                )
            }))
    );

    getActiveInstallationDetails$ = createEffect(() =>
        this.actions$.pipe(ofType(InstallationActions.getActiveInstallationDetails),
            switchMap((action) => {
                return this.installationService.getInstallationDetails(action.refCode)
                    .pipe(
                        map((installation) =>
                            InstallationActions.getActiveInstallationDetailsSuccess({ installation: installation })
                        ),
                        catchError((error) =>
                            of(InstallationActions.getActiveInstallationDetailsFailed({ error: error }))
                        )
                    )
            })
        )
    );
}
